/* Live notification bell for every portal (admin, teacher, parent).
   Polls /api/notifications, shows an unread badge, pops up a toast for each
   newly arrived notification, and deep-links to the related record. */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Bell, CheckCheck } from "lucide-react";

const POLL_MS = 25000;

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const seenIds = useRef(null); // null until first load (no toasts on first load)
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      if (seenIds.current === null) {
        seenIds.current = new Set(data.map((n) => n.id));
        return;
      }
      data.filter((n) => !n.read_status && !seenIds.current.has(n.id)).forEach((n) => {
        toast(n.title, {
          description: n.message,
          duration: 8000,
          action: n.link ? { label: "View", onClick: () => navigate(n.link) } : undefined,
        });
      });
      seenIds.current = new Set(data.map((n) => n.id));
    } catch {
      /* polling failure is non-fatal */
    }
  }, [navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((n) => !n.read_status).length;

  const openItem = async (n) => {
    if (!n.read_status) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch { /* noop */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border bg-card shadow-sm hover:bg-accent transition-colors"
          data-testid="notification-bell" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D90429] text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <p className="text-sm font-semibold">Notifications</p>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAll}
              data-testid="mark-all-read-button">
              <CheckCheck className="h-3.5 w-3.5 mr-1" />Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">No notifications yet.</p>
          ) : items.slice(0, 30).map((n) => (
            <button key={n.id} onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-accent transition-colors ${
                n.read_status ? "opacity-60" : ""}`}
              data-testid={`notification-item-${n.id}`}>
              <div className="flex items-start gap-2">
                {!n.read_status && <span className="mt-1.5 h-2 w-2 rounded-full bg-secondary shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{n.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {n.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
